


import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'

interface Article {
  id: string
  slug: string
  category: string
  title: string
  banner_image: string
  desc: string
  publish_date: string
  location: string
}

export default function CoverStorySection() {
  const [coverStory, setCoverStory] = useState<Article | null>(null)
  const [loading, setLoading] = useState(true)
  const navigate = useNavigate()

  useEffect(() => {
    const fetchCoverStory = async () => {
      try {
        const response = await fetch('/api/v1/articles')
        if (!response.ok) {
          throw new Error('Failed to fetch articles')
        }
        const articles = await response.json()

        // Sort by publish_date descending and get the latest article
        const sortedArticles = articles.sort((a: Article, b: Article) =>
          new Date(b.publish_date).getTime() - new Date(a.publish_date).getTime()
        )

        if (sortedArticles.length > 0) {
          setCoverStory(sortedArticles[0])
        }
      } catch (error) {
        console.error('Error fetching cover story:', error)
      } finally {
        setLoading(false)
      }
    }

    fetchCoverStory()
  }, [])

  const handleMouseEnter = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.currentTarget.style.opacity = '0.8'
  }

  const handleMouseLeave = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.currentTarget.style.opacity = '1'
  }

  const handleReadMore = () => {
    if (coverStory) {
      navigate(`/editorial/${coverStory.slug}`)
    }
  }

  if (loading) {
    return (
      <section className="relative w-full min-h-[400px] md:h-[560px] flex flex-col md:flex-row bg-gray-200 animate-pulse">
        <div className="w-full md:w-1/2 bg-gray-300"></div>
        <div className="w-full md:w-1/2 bg-black flex items-center justify-center px-16 md:px-16 py-16 md:py-0">
          <div className="max-w-lg">
            <div className="h-6 bg-gray-300 mb-4 w-48"></div>
            <div className="h-8 bg-gray-300 mb-4 w-64"></div>
            <div className="h-16 bg-gray-300 mb-6 w-full"></div>
            <div className="h-12 bg-gray-300 w-32"></div>
          </div>
        </div>
      </section>
    )
  }

  if (!coverStory) {
    return null
  }

  return (
    <section className="relative w-full min-h-[400px] md:h-[560px] flex flex-col md:flex-row">
      {/* Image Section */}
      <div className="w-full md:w-1/2 relative aspect-square md:h-auto">
        <img
          src={coverStory.banner_image}
          alt={coverStory.title}
          className="w-full h-full object-cover"
          style={{ position: 'absolute', top: 0, left: 0 }}
        />
      </div>

      {/* Content Section */}
      <div className="w-full md:w-1/2 bg-black flex items-center justify-center px-16 md:px-16 py-16 md:py-0">
        <div className="max-w-lg text-white">

          {/* Category */}
          <p className="italic mb-2" style={{ fontFamily: 'Cardo', fontSize: '24px', color: '#BDA36C', lineHeight: '36px' }}>
            {coverStory.category}
          </p>

          {/* Main Heading */}
          <h2 className="mb-2" style={{ fontFamily: 'Cardo', fontSize: '28px', color: '#FFFFFF', fontWeight: 'normal' }}>
            {coverStory.title}
          </h2>

          {/* Description */}
          <p className="mb-6 md:mb-10" style={{ fontFamily: 'Plus Jakarta Sans', fontSize: '14px', color: '#FFFFFF', lineHeight: '1.5' }}>
            {coverStory.desc}
          </p>

          {/* Read More Button */}
          <button
            style={{
              backgroundColor: '#FFFFFF',
              color: '#000000',
              fontFamily: 'Plus Jakarta Sans, sans-serif',
              fontSize: '14px',
              padding: '16px 28px',
              border: 'none',
              cursor: 'pointer',
              transition: 'all 0.3s ease'
            }}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            onClick={handleReadMore}
          >
            Read More
          </button>
        </div>
      </div>
    </section>
  )
}